import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Play, Voicemail as VoicemailIcon } from 'lucide-react';
import { DataTable, type Column } from '../components/data-table';
import { PageHeader, Select, Tabs } from '../components/form';
import { Alert, Badge, EmptyState } from '../components/ui';
import { api } from '../lib/api';

interface Recording {
  id: string;
  callRecordId: string | null;
  format: string;
  durationSec: number | null;
  sizeBytes: number | null;
  startedAt: string | null;
}

interface VoicemailBox {
  id: string;
  mailbox: string;
  email: string | null;
}

interface VoicemailMessage {
  id: string;
  folder: string;
  callerId: string | null;
  durationSec: number | null;
  receivedAt: string | null;
}

function clock(seconds: number | null): string {
  if (seconds === null) return '—';
  return `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, '0')}`;
}

function size(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '—';
}

/** Call recordings and voicemail, which are the two archives of call audio this PBX keeps. */
export function Recordings() {
  const [tab, setTab] = useState('recordings');

  return (
    <div className="space-y-6">
      <PageHeader
        title="Recordings and voicemail"
        description="Stored call audio. Both are private to this server and never leave it."
      />

      <Tabs
        tabs={[
          { id: 'recordings', label: 'Call recordings' },
          { id: 'voicemail', label: 'Voicemail' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'recordings' ? <RecordingList /> : <VoicemailList />}
    </div>
  );
}

function usePlayer() {
  const ref = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);

  const toggle = (id: string, src: string) => {
    const element = ref.current;
    if (!element) return;
    if (playing === id) {
      element.pause();
      setPlaying(null);
      return;
    }
    element.src = src;
    setPlaying(id);
    void element.play().catch(() => setPlaying(null));
  };

  const element = <audio ref={ref} onEnded={() => setPlaying(null)} className="hidden" />;
  return { element, playing, toggle };
}

function RecordingList() {
  const queryClient = useQueryClient();
  const player = usePlayer();

  const { data: rows = [] } = useQuery({
    queryKey: ['recordings'],
    queryFn: () => api.get<Recording[]>('/api/v1/recordings?limit=200'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/recordings/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['recordings'] }),
  });

  const columns: Array<Column<Recording>> = [
    {
      key: 'startedAt',
      header: 'Started',
      sort: (a, b) => (a.startedAt ?? '').localeCompare(b.startedAt ?? ''),
      render: (r) => when(r.startedAt),
    },
    {
      key: 'duration',
      header: 'Length',
      sort: (a, b) => (a.durationSec ?? 0) - (b.durationSec ?? 0),
      render: (r) => <span className="tabular-nums">{clock(r.durationSec)}</span>,
    },
    {
      key: 'size',
      header: 'Size',
      sort: (a, b) => (a.sizeBytes ?? 0) - (b.sizeBytes ?? 0),
      render: (r) => <span className="tabular-nums">{size(r.sizeBytes)}</span>,
    },
    { key: 'format', header: 'Format', render: (r) => <Badge>{r.format}</Badge> },
    {
      key: 'actions',
      header: 'Audio',
      align: 'right',
      render: (r) => (
        <div className="flex justify-end gap-1">
          <button
            onClick={() => player.toggle(r.id, `/api/v1/recordings/${r.id}/audio`)}
            className="rounded-[--radius] p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Play recording"
            title="Play"
          >
            <Play size={15} className={player.playing === r.id ? 'text-primary' : undefined} />
          </button>
          <a
            href={`/api/v1/recordings/${r.id}/audio`}
            download
            className="rounded-[--radius] p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Download recording"
            title="Download"
          >
            <Download size={15} />
          </a>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {player.element}

      {rows.length === 0 && (
        <Alert tone="warning">
          No recordings yet. Recording is off for every extension by default — turn it on per
          extension under Extensions, then place a call.
        </Alert>
      )}

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(r) => r.id}
        search={(r) => `${r.startedAt ?? ''} ${r.format}`}
        searchPlaceholder="Search recordings"
        onDelete={(selection) => selection.forEach((r) => remove.mutate(r.id))}
        deleteLabel={(selection) =>
          `Delete ${selection.length} recording(s)? The audio is removed from disk as well.`
        }
        empty="No recordings stored."
      />
    </div>
  );
}

function VoicemailList() {
  const queryClient = useQueryClient();
  const player = usePlayer();
  const [boxId, setBoxId] = useState('');

  const { data: boxes = [] } = useQuery({
    queryKey: ['voicemail-boxes'],
    queryFn: () => api.get<VoicemailBox[]>('/api/v1/voicemail/boxes'),
  });

  const selected = boxId || boxes[0]?.id || '';

  const { data: messages = [] } = useQuery({
    queryKey: ['voicemail-messages', selected],
    queryFn: () => api.get<VoicemailMessage[]>(`/api/v1/voicemail/boxes/${selected}/messages`),
    enabled: selected !== '',
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/voicemail/messages/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['voicemail-messages'] }),
  });

  const columns: Array<Column<VoicemailMessage>> = [
    {
      key: 'receivedAt',
      header: 'Received',
      sort: (a, b) => (a.receivedAt ?? '').localeCompare(b.receivedAt ?? ''),
      render: (m) => when(m.receivedAt),
    },
    {
      key: 'callerId',
      header: 'From',
      sort: (a, b) => (a.callerId ?? '').localeCompare(b.callerId ?? ''),
      render: (m) => m.callerId ?? 'Unknown',
    },
    {
      key: 'folder',
      header: 'Folder',
      render: (m) => <Badge tone={m.folder === 'new' ? 'success' : 'muted'}>{m.folder}</Badge>,
    },
    {
      key: 'duration',
      header: 'Length',
      sort: (a, b) => (a.durationSec ?? 0) - (b.durationSec ?? 0),
      render: (m) => <span className="tabular-nums">{clock(m.durationSec)}</span>,
    },
    {
      key: 'actions',
      header: 'Audio',
      align: 'right',
      render: (m) => (
        <div className="flex justify-end gap-1">
          <button
            onClick={() => player.toggle(m.id, `/api/v1/voicemail/messages/${m.id}/audio`)}
            className="rounded-[--radius] p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Play message"
            title="Play"
          >
            <Play size={15} className={player.playing === m.id ? 'text-primary' : undefined} />
          </button>
          <a
            href={`/api/v1/voicemail/messages/${m.id}/audio`}
            download
            className="rounded-[--radius] p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Download message"
            title="Download"
          >
            <Download size={15} />
          </a>
        </div>
      ),
    },
  ];

  if (boxes.length === 0) {
    return (
      <EmptyState
        title="No mailboxes"
        hint="A mailbox is created with each extension that has voicemail enabled."
      />
    );
  }

  return (
    <div className="space-y-4">
      {player.element}

      <div className="flex items-center gap-2">
        <VoicemailIcon size={16} className="text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Mailbox</span>
        <div className="w-48">
          <Select value={selected} onChange={setBoxId}>
            {boxes.map((b) => (
              <option key={b.id} value={b.id}>
                {b.mailbox}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <DataTable
        rows={messages}
        columns={columns}
        rowKey={(m) => m.id}
        search={(m) => `${m.callerId ?? ''} ${m.folder}`}
        searchPlaceholder="Search messages"
        onDelete={(selection) => selection.forEach((m) => remove.mutate(m.id))}
        deleteLabel={(selection) =>
          `Delete ${selection.length} message(s)? This also removes them from the phone's mailbox.`
        }
        empty="No messages in this mailbox."
      />
    </div>
  );
}
